import moment from "moment-timezone";
import JSZip from "jszip";
import { saveAs } from "file-saver";

import { ApiHook } from "services/Api";
import { NotificationManagerAPI } from "notifications/NotificationManager";
import { getUserTimezone } from "utility/UserUtils";

import * as RD from "reports/ReportDef";
import * as RP from "reports/ReportPdf";

const dateDisplay = (day: Date): string => {
    return moment(day).tz(getUserTimezone()).format("MM-DD-YYYY");
};

const pdfFileName = (report) => {
    const {
        patient_name_masked: patientNameMasked,
        patient_id: patientId,
        payer_type: payerType,
        report_week,
        included_weeks,
        approved_date
    } = report;

    // The 'included_weeks' field in the OutcomeReport model currently has overloaded use
    // as both the number of coaching plan weeks included in the report and the report week.
    // For the latter use case, it is inaccurate, as the report week may be greater than the
    // actual number of plan weeks included in the report. This field will ultimately be renamed
    // to 'plan_weeks_in_report' to reflect the former use case and 'report_week' will be used
    // for the latter use case.
    let reportWeek: RD.ReportWeek = report_week || Number(included_weeks);
    return RP.generateReportPdfFileName(patientNameMasked, reportWeek, false, approved_date, patientId, payerType);
};

export const downloadReports = async(
    componentName: string,
    patientId: number,
    reportId: number,
    notificationManager: NotificationManagerAPI,
    API: ApiHook
) => {

    notificationManager.clearAllNotifications();

    try {
        if (!patientId && !reportId) {
            throw new Error("[ReportsDownloader] Missing patientId or reportId prop.");
        }
        else if (patientId && reportId) {
            throw new Error("[ReportsDownloader] Cannot provide both patientId and reportId props.");
        }

        let reportsUrl = `/outcome_reports/${reportId ? `${reportId}/?pdf_data=true` : `?patient_id=${patientId}&pdf_data=true`}`;

        let downloadNotificationId: string;
        if (notificationManager) {
            downloadNotificationId = notificationManager.addCustom("Downloading Reports...", { duration: 10 });
        }

        await API.api_Get(
            componentName,
            reportsUrl,
            notificationManager,
            (response) => {
                const reportsData = response?.data;

                if (!reportsData) {
                    throw new Error(`No report PDFs found at url[${reportsUrl}]`);
                }

                const multipleReports = "results" in reportsData;
                const allReports = multipleReports ? reportsData.results : [reportsData];
                const reports = allReports.filter(report => { return report.status === "Approved" && report.pdf_data});
                const reportCount = reports.length;

                if (!reportCount) {
                    throw new Error(`No report approved reports with PDFs found at url[${reportsUrl}]`);
                }

                if (notificationManager && downloadNotificationId) {
                    notificationManager.updateNotification(downloadNotificationId, `Downloading ${reportCount} report${reportCount === 1 ? "" : "s"}...`)
                }

                if (reportCount === 1) {
                    const report = reports[0];

                    try {
                        const base64Content = report.pdf_data.split("base64,")[1] || report.pdf_data;
                        const binaryData = atob(base64Content);
                        const byteArray = new Uint8Array(binaryData.length);
                        for (let i = 0; i < binaryData.length; i++) {
                            byteArray[i] = binaryData.charCodeAt(i);
                        }
                        const pdfBlob = new Blob([byteArray], { type: "application/pdf" });
                        const filename = pdfFileName(report);
                        console.log(`[ReportsDownloader] Attempting to download: ${filename}, blob size: ${pdfBlob.size} bytes`);
                        saveAs(pdfBlob, filename);
                    } catch (error) {
                        console.error("[ReportsDownloader] Error processing PDF data:", error);
                        throw new Error(`Failed to process PDF data: ${error.message}`);
                    }
                }
                else {
                    let zip = new JSZip();
                    let pdf = zip.folder("pdf");

                    reports.forEach(
                        (report, index: number) => {
                            let uri = report.pdf_data;
                            let idx = uri.indexOf("base64,") + "base64,".length;
                            let content = uri.substring(idx);
                            pdf.file(`${index+1}_${pdfFileName(report)}`, content, { base64: true });
                        }
                    );

                    // For ZIP filename, synthesize identifier from first report
                    const firstReport = reports[0];
                    const zipIdentifier = firstReport.payer_type === RD.PayerEnums.VA
                        ? `Patient${firstReport.patient_id}`
                        : firstReport.patient_name_masked.replace(/ /g, "_");

                    zip.generateAsync({ type: "blob" })
                        .then(
                            (content) => {
                                saveAs(content, `Outcomes_${zipIdentifier}_${dateDisplay(new Date())}.zip`);
                            }
                        );
                }

                if (notificationManager && downloadNotificationId) {
                    notificationManager.updateNotification(downloadNotificationId, `Successfully downloaded ${reportCount} report${reportCount === 1 ? "" : "s"}.`)
                }
            }
        );
    }
    catch (error) {
        console.error(error);
        notificationManager?.addError(error);
    }
};
